/**
 * Error capture for the frontend.
 *
 * Errors are logged to the console. Nothing leaves the browser
 * unless a reporter is registered with `setExceptionReporter`. The
 * app registers one on production and canary that sends errors to
 * our self-hosted error tracker.
 */

/**
 * Extra detail recorded alongside the error.
 *
 * On production and canary this is sent to our error tracker, along
 * with the error's message. Never put anything the user typed into
 * either (names, emails, notes, form values): send ids and statuses
 * that explain what the app was doing, not the user's content.
 */
export interface CaptureExceptionContext {
  /** Values that help explain what the app was doing. */
  data?: Record<string, unknown>
}

export type ExceptionReporter = (
  error: unknown,
  context?: CaptureExceptionContext
) => void

let reporter: ExceptionReporter | undefined

/**
 * Register the service that errors are forwarded to. Pass
 * `undefined` to go back to console-only logging.
 */
export function setExceptionReporter(
  next: ExceptionReporter | undefined
) {
  reporter = next
}

/**
 * Record an error that the app handled but should not have hit.
 *
 * Don't build the error's message from user-entered values; see
 * `CaptureExceptionContext`.
 *
 * A failure inside the reporter is swallowed — reporting must never
 * take down the code path that was trying to report.
 */
export function captureException(
  error: unknown,
  context?: CaptureExceptionContext
) {
  if (context?.data) {
    console.error(error, context.data)
  } else {
    console.error(error)
  }

  if (!reporter) return
  try {
    reporter(error, context)
  } catch (reporterError) {
    console.error('Failed to report an exception', reporterError)
  }
}
